import { generatePath } from 'react-router';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQueries } from '@tanstack/react-query';

import type { OrgUnitSimple } from 'src/types/orgUnit';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';

interface SelectedOrgUnitWithAncestors {
  isLoading: boolean;
  data: OrgUnitSimple[][] | undefined;
}

export const useSelectedOrgUnitsWithAncestors = (
  selectedOrgUnitIds: number[],
  surveyId?: number,
): SelectedOrgUnitWithAncestors => {
  // Execute all queries in parallel
  const results: UseQueryResult<OrgUnitSimple[][]>[] = useQueries({
    queries: selectedOrgUnitIds.map((orgUnitId) => {
      const endpoint = generatePath(apiPaths.ORG_UNIT_ANCESTORS_BY_SURVEY, {
        orgUnitId: orgUnitId.toString(),
        surveyId: surveyId?.toString(),
      });

      return {
        queryKey: ['orgUnitAncestors', surveyId, orgUnitId],
        queryFn: async () => {
          try {
            const response = await HttpService.get(endpoint, {}, false);
            return response.data;
          } catch (error) {
            console.error(
              `Error fetching ancestors for org unit ${orgUnitId}:`,
              error,
            );
            throw error;
          }
        },
        // Cache for 30 minutes
        cacheTime: 1800000,
        staleTime: 1800000,
        enabled: !!surveyId && !!orgUnitId,
      };
    }),
  });

  // Return an object that conforms to the SelectedOrgUnitWithAncestors interface
  return {
    // Set isLoading to true if any query is loading
    isLoading:
      selectedOrgUnitIds.length > 0 &&
      results.some((query) => query.isLoading || query.isFetching),

    // Combine all successful query results into a single array
    data:
      selectedOrgUnitIds.length === 0 ||
      results.some((query) => query.isLoading || query.isFetching)
        ? undefined
        : results
            .filter((query) => query.isSuccess && query.data)
            // The data is an array of arrays, where the first element of each inner array is the org unit we want,
            // the rest are siblings used for the tree structure
            .map<OrgUnitSimple[]>(
              (query) => query.data?.map((orgUnits) => orgUnits[0]) ?? [],
            ),
  };
};
